/**
 * InvestorPortalAuthContext — magic-link session for investors.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  INV_PORTAL_API,
  INV_REFRESH_FAILED,
  investorPortalApi,
  setInvAccessToken,
} from "@/lib/investorPortalApi";
import silentRefresh from "@/lib/silentRefresh";

const Ctx = createContext(null);

const needsInvestorRefresh = (pathname) => pathname.startsWith("/portal/investor");

export const InvestorPortalAuthProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [phase, setPhase] = useState(() =>
    needsInvestorRefresh(typeof window !== "undefined" ? window.location.pathname : "") ? "checking" : "anonymous",
  );
  const [session, setSession] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!needsInvestorRefresh(pathname)) {
      setInvAccessToken(null);
      setSession(null);
      setPhase("anonymous");
      return undefined;
    }

    (async () => {
      const data = await silentRefresh(`${INV_PORTAL_API}/session/refresh`);
      if (cancelled) return;
      if (!data?.access_token) {
        setInvAccessToken(null);
        setPhase("anonymous");
        return;
      }
      setInvAccessToken(data.access_token);
      try {
        const { data: me } = await investorPortalApi.get("/me");
        if (cancelled) return;
        setSession(me);
        setPhase("authenticated");
      } catch (_e) {
        if (cancelled) return;
        setInvAccessToken(null);
        setPhase("anonymous");
      }
    })();

    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    const onFail = () => {
      setInvAccessToken(null);
      setSession(null);
      setPhase("anonymous");
    };
    window.addEventListener(INV_REFRESH_FAILED, onFail);
    return () => window.removeEventListener(INV_REFRESH_FAILED, onFail);
  }, []);

  const completeLogin = useCallback(async (verifyResponse) => {
    setInvAccessToken(verifyResponse.access_token);
    const { data: me } = await investorPortalApi.get("/me");
    setSession(me);
    setPhase("authenticated");
    return me;
  }, []);

  const refreshSession = useCallback(async () => {
    const { data: me } = await investorPortalApi.get("/me");
    setSession(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try { await investorPortalApi.post("/logout"); } catch (_e) { /* ignore */ }
    setInvAccessToken(null);
    setSession(null);
    setPhase("anonymous");
  }, []);

  return (
    <Ctx.Provider value={{ phase, session, isAuthenticated: phase === "authenticated", completeLogin, refreshSession, logout }}>
      {children}
    </Ctx.Provider>
  );
};

export const useInvestorPortalAuth = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useInvestorPortalAuth() must be used inside <InvestorPortalAuthProvider>");
  return ctx;
};

export default InvestorPortalAuthProvider;
